/* jshint esversion: 6 */
import React from 'react';
import PropTypes from 'prop-types';
import Content from 'app/components/common/Content.jsx';
import SocialEditorTabs from 'app/components/EditorComponents/SocialEditorTabs.jsx';
import FormUtilityMixin from 'app/mixins/FormUtilityMixin.js';
import ConstantMixin from 'app/mixins/ConstantMixin.js';
import DeleteModal from 'app/components/common/DeleteModal.jsx';
import ShortenerProvider from 'app/redux/Providers/ShortenerProvider.jsx';
import DomainProvider from 'app/redux/Providers/DomainProvider.jsx';
import PersistData from 'app/mixins/PersistData.jsx';
import * as _ from 'underscore';
import { compose } from 'redux';
import { History } from 'lucide-react';
import Utilities from 'app/utility/Utilities.js';
import { Outlet } from 'react-router-dom';
import ComponentWithContext from 'app/components/Routers/ComponentWithContext.jsx';
import { SOCIAL_DELETE, SOCIAL_UPDATE } from 'app/redux/constants.js';
import SocialSettings from '@/shadcn/components/settings/social-settings.tsx';
import {
    buildSocialSections,
    destinationNetwork,
    SOCIAL_COMPOSITE_PATHS,
    SOCIAL_ERROR_KEYS
} from '@/lib/social-settings-sections.ts';
import {
    dirtySectionIds,
    sectionErrorCounts,
    sectionPaths,
    SETTINGS_NOTE_KEY,
    validationKeys,
    validationTree
} from '@/lib/settings-sections.ts';
import { socialWrites } from '@/lib/settings-changes.ts';

/**
 * The tab a URL names, and the URL a tab wants. Same translation as
 * `InputEdit.jsx`: the routes stay so deep links keep working, the shell
 * selects by `activeSection`. Only the Post options tab is renamed, because
 * legacy's route is `options` where the section id is `post-options`.
 */
const SECTION_BY_PATH = {
    options: 'post-options',
    links: 'links',
    advanced: 'advanced'
};

const PATH_BY_SECTION = _.invert(SECTION_BY_PATH);

class SocialEditor extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            socialId: 0,
            social: {},
            cleanSocial: {},
            errors: null,
            buttonParams: {},
            youTubeFetched: false,
            noteDismissed: false,
            shown: false
        };

        this.updateSocial = this.updateSocial.bind(this);
        this.deleteSocial = this.deleteSocial.bind(this);
        this.cancelEdit = this.cancelEdit.bind(this);
        this.selectSection = this.selectSection.bind(this);
        this.onSocialWritten = this.onSocialWritten.bind(this);

        /*
         * One handler, two actions, so two descriptors — and they are held rather
         * than rebuilt, because unregistering matches the object that registered.
         */
        this.writeCallbacks = [
            {action: SOCIAL_UPDATE, func: this.onSocialWritten},
            {action: SOCIAL_DELETE, func: this.onSocialWritten}
        ];
        this.onFieldChange = this.onFieldChange.bind(this);
        this.dismissNote = this.dismissNote.bind(this);

    }

    static getDerivedStateFromProps(props, state) {
        let nextState = {};

        // a record already in the store can be an earlier visit's, changed since, so the draft waits for this one's show
        if (state.socialId && state.shown && _.isEmpty(state.social)) {
            for (let i = 0; i < props.socials.list.length; ++i) {
                if (props.socials.list[i].id == state.socialId && props.socials.list[i].params !== undefined) {
                    nextState.social = Utilities.deepClone(props.socials.list[i]);
                    nextState.cleanSocial = Utilities.deepClone(props.socials.list[i]);
                    break;
                }
            }
        }

        let errors = props.getApiErrors(['SOCIAL_CREATE', 'SOCIAL_UPDATE', 'SOCIAL_DELETE']);
        if (errors.length) {
            nextState.errors = errors;
        } else if (state.errors && !errors.length) {
            nextState.errors = null;
        }

        return (Object.keys(nextState).length) ? nextState : null;

    }

    componentDidMount() {
        let socialId = 0;

        socialId = this.props.router.params.socialId;

        // no id, redirect them to the list of socials
        if (!socialId) {
            this.props.router.navigate('/config/socials');
        }

        this.setState({
            socialId: socialId,
            buttonParams: {socialId: socialId}
        });

        if (this.props.app.firstLoad.SOCIAL_LIST === false) {
            this.props.getSocials();
        }

        this.props.getSocial(socialId);

        // The Links tab's one select offers both, so both lists have to be here
        // before it renders. HelperPanel fetched them from its own constructor;
        // the select is built from data now, so the fetch moves up with it.
        this.props.getShorteners();
        this.props.getDomains();

        this.setState({
            noteDismissed: this.props.getPersistedData(SETTINGS_NOTE_KEY.destination) === true
        });

        this.writeCallbacks.map((callback) => { this.props.registerCallback(callback); });
    }

    componentWillUnmount() {
        this.writeCallbacks.map((callback) => { this.props.unregisterCallback(callback); });
    }

    /*
     * This screen edits one social and has nowhere to go once that social has
     * been saved or removed, so either answer sends the user back to the list.
     *
     * Errors keep them here to see them: the callback fires whether the write
     * succeeded or not, and `state.errors` is what getDerivedStateFromProps has
     * already made of the same response.
     */
    onSocialWritten() {
        if (this.state.errors === null) {
            this.props.router.navigate('/config/socials');
        }
    }

    /*
     * ONLY THE YOUTUBE FETCH LIVES HERE NOW. The write side of this method —
     * watching the SOCIAL_UPDATE / SOCIAL_DELETE loading flags to decide when
     * to navigate away — is `onSocialWritten`'s job, registered above.
     *
     * This is not that. It is a READ triggered by the record arriving, and
     * `psID` is not known at mount because the social loads async, so there is
     * nowhere earlier to put it.
     */
    componentDidUpdate(prevProps, prevState) {

        if (prevProps.app.loading.SOCIAL_SHOW === true && this.props.app.loading.SOCIAL_SHOW === false && !this.state.shown) {
            this.setState({shown: true});
        }

        // YouTube's Category and Playlists fields are the only two whose options
        // come from the network rather than from constants, and legacy fetched
        // them from YouTubeOptions' own componentDidMount. That panel no longer
        // renders, so the fetch moves here — once, and only once the record has
        // arrived and says it is a YouTube channel.
        if (!this.state.youTubeFetched &&
            parseInt(this.state.social.psID, 10) === this.props.app.config.constants.POST_SVC_YOUTUBE) {
            this.setState({youTubeFetched: true}, () => {
                this.props.getYouTubeCategories(this.state.social.id);
                this.props.getYouTubePlaylists(this.state.social.id);
            });
        }

        if ((prevProps.app.loading.SOCIAL_UPDATE === true && this.props.app.loading.SOCIAL_UPDATE === false) ||
            (prevProps.app.loading.SOCIAL_DELETE === true && this.props.app.loading.SOCIAL_DELETE === false)) {
            if (this.state.errors === null) {
                this.props.router.navigate('/config/socials');
            }
        }

    }

    /**
     * Every field-level edit on the settings screen. `socialWrites` turns what
     * the screen reported into dot paths and values; each goes through the same
     * `FormUtilityMixin.requestChange` the legacy panels use, which mutates and
     * re-sets `this.state` in place — so the shortener select's pair of writes,
     * one clearing the other id, compounds correctly across the loop.
     */
    onFieldChange(change) {

        socialWrites(change, this.state.social).forEach((entry) => {
            FormUtilityMixin.requestChange.call(this, {
                currentTarget: {dataset: {field: entry.field}},
                target: {value: entry.value}
            });
        });

    }

    dismissNote() {

        this.props.persistData(SETTINGS_NOTE_KEY.destination, true);
        this.setState({noteDismissed: true});

    }

    updateSocial() {

        this.props.deleteApiError(['SOCIAL_UPDATE']);
        this.props.updateSocial(this.state.social);

    }

    deleteSocial() {

        let cancelDelete = () => {
                this.props.closeAllModals();
            },
            confirmDelete = (e) => {
                e.preventDefault();
                this.props.deleteSocial(this.state.social.id);
                this.props.closeAllModals();
            },
            modal = (
                <DeleteModal
                    modalCopy={"Are you sure you want to remove this social?"}
                    cancelAction={cancelDelete}
                    confirmAction={confirmDelete} />
            );

        this.props.addModal(modal);

    }

    cancelEdit() {

        this.props.router.navigate('/config/socials');

    }

    /** Which settings tab the current URL names. */
    currentSection() {

        let segment = this.props.router.location.pathname.split('/').pop();

        return SECTION_BY_PATH[segment] || 'post-options';

    }

    selectSection(id) {

        this.props.router.navigate([
            '/config/social',
            this.state.social.id,
            PATH_BY_SECTION[id] || 'options'
        ].join('/'));

    }

    render() {
        let header = null,
            {children, ...otherProps} = this.props;

        header = (
            <SocialEditorTabs
                {...this.props}
                socialType={this.state.social.psID}
                socialLabel={this.state.social.name} />
        );

        // The error log is a report rather than a setting: no tab strip, no save,
        // no delete. It keeps the legacy panel and its outlet context.
        if (this.props.router.location.pathname.match(/log$/)) {
            otherProps.social = this.state.social;
            otherProps.changeHandler = FormUtilityMixin.requestChange.bind(this);
            otherProps.activePanel = 'log';
            otherProps.fieldMapping = "social.params";
            otherProps.errorMapping = "params";

            return (
                <Content header={header} main={<Outlet context={otherProps}/>}/>
            );
        }

        return (
            <Content header={header} main={this.renderSettings()}/>
        );
    }

    renderSettings() {

        let social = this.state.social,
            loading = _.isEmpty(social) || social.params === undefined,
            network = destinationNetwork(social),
            sections = buildSocialSections(social, {
                constants: this.props.app.config.constants,
                feedUrlBase: this.props.app.config.feedUrl,
                shorteners: this.props.shorteners || [],
                domains: this.props.domains || [],
                shortenServiceName: ConstantMixin.getShortenServiceByCode,
                categories: this.props.socials.categories || {},
                playlists: this.props.socials.playlists || {},
                validation: validationTree(this.props.app),
                advancedView: this.props.app.flags.routeViewType === this.props.app.config.constants.ROUTE_VIEW_ADVANCED,
            }),
            paths = sectionPaths(sections, 'social', SOCIAL_COMPOSITE_PATHS);

        return (
            <div className="tw-root">
                <SocialSettings
                    sections={sections}
                    network={network}
                    accountName={social.name || ''}
                    avatarUrl={social.icon || social.src || undefined}
                    loading={loading}
                    formKey={String(social.id || '')}
                    onChange={this.onFieldChange}
                    activeSection={this.currentSection()}
                    onSectionChange={this.selectSection}
                    dirty={!_.isEqual(this.state.cleanSocial, social)}
                    dirtySections={dirtySectionIds(paths, social, this.state.cleanSocial)}
                    errorCounts={sectionErrorCounts(validationKeys(this.props.app), SOCIAL_ERROR_KEYS)}
                    saving={this.props.app.loading.SOCIAL_UPDATE === true}
                    formError={this.state.errors !== null
                        ? "We couldn't complete that update. Check the highlighted tabs for details."
                        : undefined}
                    backLabel="Outputs"
                    onBack={this.cancelEdit}
                    /* `null` suppresses it. Undismissed passes undefined, so the
                       screen keeps its own rule of hiding the note on Links —
                       where "only applies to automated posts" would be false. */
                    note={this.state.noteDismissed ? null : undefined}
                    onDismissNote={this.dismissNote}
                    actions={[
                        {
                            label: 'Error log',
                            icon: <History/>,
                            onClick: () => this.props.router.navigate((this.props.appName === 'client' ? '/social/' : '/config/social/') + social.id + '/log')
                        }
                    ]}
                    onSave={this.updateSocial}
                    onCancel={this.cancelEdit} />
            </div>
        );

    }

};

SocialEditor.displayName = "SocialEditor";

SocialEditor.defaultProps = {
    appName: "dlvr"
};

const enhance = compose(
    ComponentWithContext,
    PersistData,
    ShortenerProvider,
    DomainProvider
);

export default enhance(SocialEditor);
